import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';

// ⚠️ CRITICAL: Import from parent shell, NOT local copy
import { useRetroContextSafe } from '@vortx/shared-ui';

import {
    PhdDashboard,
    ForecastingSection,
    WikiSection,
    ResearchSection,
    CalibrationWizard,
    LearningSection,
} from '../components';

/**
 * FluxPhdApp - Flux PhD Central Intelligence Platform
 *
 * Project-independent: benchmarks, model training, forecasting, wiki.
 * Uses RetroContext from parent flux-web shell for navigation state.
 */
function FluxPhdInternal() {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { phdSection, setPhdSection } = useRetroContextSafe();

    // Sync section from URL on mount:
    // 1. Path-based: /phd/research → 'research'
    // 2. Query-based: ?section=forecasting → 'forecasting'
    useEffect(() => {
        const knownSections = ['dashboard', 'research', 'forecasting', 'learning', 'wiki', 'calibration'];
        const pathMatch = location.pathname.match(/^\/phd\/(\w+)/);
        if (pathMatch && knownSections.includes(pathMatch[1]) && pathMatch[1] !== phdSection) {
            setPhdSection(pathMatch[1]);
            return;
        }

        const urlSection = searchParams.get('section');
        if (urlSection && urlSection !== phdSection && knownSections.includes(urlSection)) {
            setPhdSection(urlSection);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Section transition
    const [sectionTransitioning, setSectionTransitioning] = useState(false);
    const prevSectionRef = useRef(phdSection);

    useEffect(() => {
        if (prevSectionRef.current !== phdSection) {
            setSectionTransitioning(true);
            const timer = setTimeout(() => {
                setSectionTransitioning(false);
                prevSectionRef.current = phdSection;
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [phdSection]);

    // Render active section
    const renderMainContent = () => {
        const section = phdSection || 'dashboard';

        switch (section) {
            case 'dashboard':
                return <PhdDashboard />;
            case 'forecasting':
                return <ForecastingSection />;
            case 'wiki':
                return <WikiSection />;
            case 'research':
                return <ResearchSection />;
            case 'learning':
                return <LearningSection />;
            case 'calibration':
                return <CalibrationWizard />;
            default:
                return <PhdDashboard />;
        }
    };

    return (
        <Box sx={{
            height: '100%',
            transition: 'opacity 0.2s ease, transform 0.2s ease',
            opacity: sectionTransitioning ? 0 : 1,
            transform: sectionTransitioning ? 'translateY(4px)' : 'translateY(0)',
        }}>
            {renderMainContent()}
        </Box>
    );
}

/**
 * FluxPhdApp - Project-independent PhD platform
 */
export default function FluxPhdApp() {
    return <FluxPhdInternal />;
}
